import { datadogRum } from "@datadog/browser-rum";

export type FeatureFlagValues<T extends string> = Record<T, boolean>;

interface FeatureFlagStoragePayload<T extends string> {
  version: number;
  values: FeatureFlagValues<T>;
  updatedAt: number;
}

interface NormalizedStorageValue<T extends string> {
  payload: FeatureFlagStoragePayload<T>;
  migrated: boolean;
}

export interface FeatureFlagServiceOptions<T extends string> {
  storageKey: string;
  storageVersion: number;
  flagKeys: T[];
  defaultValues: FeatureFlagValues<T>;
  logLabel: string;
}

export class FeatureFlagService<T extends string> {
  protected values: FeatureFlagValues<T>;
  protected hasResolvedValues = false;
  private readonly flagKeys: T[];
  private readonly flagSet: Set<T>;
  private readonly storageKey: string;
  private readonly storageVersion: number;
  private readonly defaultValues: FeatureFlagValues<T>;
  private readonly logLabel: string;
  private readonly isBrowser: boolean;
  private readonly storageAvailable: boolean;

  constructor({
    storageKey,
    storageVersion,
    flagKeys,
    defaultValues,
    logLabel,
  }: FeatureFlagServiceOptions<T>) {
    this.storageKey = storageKey;
    this.storageVersion = storageVersion;
    this.flagKeys = flagKeys;
    this.flagSet = new Set(flagKeys);
    this.defaultValues = defaultValues;
    this.logLabel = logLabel;
    this.isBrowser =
      typeof window !== "undefined" && typeof window.document !== "undefined";
    this.storageAvailable =
      this.isBrowser && typeof window.localStorage !== "undefined";
    this.values = { ...defaultValues };

    this.hydrateFromStorage();

    if (this.isBrowser) {
      window.addEventListener("storage", this.handleStorageEvent);
    }
  }

  getFlag(flagKey: T): boolean {
    return this.values[flagKey] ?? this.defaultValues[flagKey];
  }

  /**
   * Whether the values behind `getFlag` are real yet. `getFlag` cannot say
   * "not yet" — it falls back to the declared default — so a caller whose
   * decision is irreversible has to ask this first. True once a payload has
   * been applied from PostHog, hydrated from storage, or fallen back to
   * defaults after an error.
   */
  hasResolved(): boolean {
    return this.hasResolvedValues;
  }

  protected applyFallbackDefaults(
    reason: string,
    metadata?: Record<string, unknown>
  ): void {
    this.persistValues(this.defaultValues);
    this.logAction(reason, { ...(metadata ?? {}), fallbackApplied: true });
  }

  protected persistValues(values: Partial<FeatureFlagValues<T>>): void {
    const merged = this.mergeWithDefaults(values);
    this.values = merged;
    this.hasResolvedValues = true;

    const payload: FeatureFlagStoragePayload<T> = {
      version: this.storageVersion,
      values: merged,
      updatedAt: Date.now(),
    };

    this.persistPayload(payload);
  }

  protected mergeWithDefaults(
    values: Partial<FeatureFlagValues<T>>
  ): FeatureFlagValues<T> {
    return this.flagKeys.reduce((acc, key) => {
      acc[key] =
        key in values ? (values[key] as boolean) : this.defaultValues[key];
      return acc;
    }, {} as FeatureFlagValues<T>);
  }

  private hydrateFromStorage(): void {
    if (!this.storageAvailable) {
      this.logAction("Feature flag storage unavailable; using defaults");
      return;
    }

    try {
      const raw = window.localStorage.getItem(this.storageKey);
      if (!raw) {
        this.logAction("No cached feature flags found; using defaults");
        return;
      }

      const parsed = JSON.parse(raw);
      const normalized = this.normalizeStoredValue(parsed);

      if (!normalized) {
        this.logAction(
          "Cached feature flags were invalid; falling back to defaults"
        );
        return;
      }

      this.values = normalized.payload.values;
      this.hasResolvedValues = true;

      if (normalized.migrated) {
        this.persistPayload(normalized.payload);
        this.logAction("Migrated legacy feature flag cache", {
          updatedAt: normalized.payload.updatedAt,
        });
      }
    } catch (error) {
      this.logError("Failed to hydrate feature flags", error);
    }
  }

  private persistPayload(payload: FeatureFlagStoragePayload<T>): void {
    if (!this.storageAvailable) {
      return;
    }

    try {
      window.localStorage.setItem(this.storageKey, JSON.stringify(payload));
    } catch (error) {
      this.logError("Failed to persist feature flags", error, {
        payloadSize: JSON.stringify(payload).length,
      });
    }
  }

  private normalizeStoredValue(
    value: unknown
  ): NormalizedStorageValue<T> | null {
    if (!value || typeof value !== "object") {
      return null;
    }

    const record = value as Record<string, unknown>;
    if (
      "version" in record &&
      typeof record.version === "number" &&
      "values" in record
    ) {
      const rawUpdatedAt = record.updatedAt;

      return {
        payload: {
          version: record.version,
          values: this.mergeWithDefaults(
            this.pickKnownFlags(record.values as Record<string, unknown>)
          ),
          updatedAt:
            typeof rawUpdatedAt === "number" ? rawUpdatedAt : Date.now(),
        },
        migrated: false,
      };
    }

    const legacyValues = this.pickKnownFlags(record);
    if (Object.keys(legacyValues).length === 0) {
      return null;
    }

    return {
      payload: {
        version: this.storageVersion,
        values: this.mergeWithDefaults(legacyValues),
        updatedAt: Date.now(),
      },
      migrated: true,
    };
  }

  private pickKnownFlags(
    values: Record<string, unknown>
  ): Partial<FeatureFlagValues<T>> {
    return Object.entries(values).reduce(
      (acc, [key, value]) => {
        if (this.flagSet.has(key as T) && typeof value === "boolean") {
          acc[key as T] = value;
        }
        return acc;
      },
      {} as Partial<FeatureFlagValues<T>>
    );
  }

  private handleStorageEvent = (event: StorageEvent): void => {
    if (!event.key || event.key !== this.storageKey || !event.newValue) {
      return;
    }

    try {
      const parsed = JSON.parse(event.newValue);
      const normalized = this.normalizeStoredValue(parsed);

      if (!normalized) {
        return;
      }

      this.values = normalized.payload.values;
      this.hasResolvedValues = true;
    } catch (error) {
      this.logError("Failed to process feature flag storage event", error);
    }
  };

  protected logAction(
    message: string,
    context?: Record<string, unknown>
  ): void {
    const payload = { message, ...(context ?? {}) };
    console.log(`[${this.logLabel}] ${message}`, context);
    datadogRum.addAction(this.logLabel, payload);
  }

  protected logError(
    message: string,
    error: unknown,
    context?: Record<string, unknown>
  ): void {
    console.error(`[${this.logLabel}] ${message}`, error, context);
    const err =
      error instanceof Error ? error : new Error(String(error ?? message));
    datadogRum.addError(err, { message, ...(context ?? {}) });
  }
}
